"use client"

import { useEffect, useState } from "react"
import { useTranslations } from "next-intl"
import { Typography } from "@/shared/ui/atoms"
import { DemandSignalButtons } from "@/features/demand-signal"

interface HospitalMobileActionBarProps {
  name: string
  tel: string
  fullAddress: string
  /**
   * 수요 조사 파일럿 병원이면 길찾기·전화 대신 수요 조사 버튼을 싣는다(B2C-475).
   * 360px 에 네 버튼은 들어가지 않고, 미입점 병원에서는 수요 조사가 주 액션이다.
   * 전화·길찾기는 히어로와 우측 정보판에 경로가 남는다.
   */
  demandSignalHospitalOriginId?: number
}

/**
 * 좁은 화면에서 본문을 한참 내려간 뒤에도 "전화·길찾기"가 손에 닿게 하는 고정 바.
 *
 * 히어로가 화면에 남아 있는 동안에는 같은 버튼이 두 벌 보이므로 띄우지 않는다 —
 * 히어로를 지나쳤을 때만 올라온다. 데스크톱은 오른쪽 정보판이 그 역할을 하므로 숨긴다.
 */
const HospitalMobileActionBar = ({
  name,
  tel,
  fullAddress,
  demandSignalHospitalOriginId,
}: HospitalMobileActionBarProps) => {
  const t = useTranslations()
  const [isVisible, setIsVisible] = useState(false)
  const mapQuery = encodeURIComponent(`${name} ${fullAddress}`.trim())

  useEffect(() => {
    const hero = document.querySelector("#hospital-detail-page > header")
    if (!hero || typeof IntersectionObserver === "undefined") return

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(!entry.isIntersecting),
      { threshold: 0 },
    )
    observer.observe(hero)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-[90] border-t border-gray-100 bg-white/95 px-4 py-3 backdrop-blur transition-transform duration-200 lg:hidden ${
        isVisible ? "translate-y-0" : "translate-y-full"
      }`}
      aria-hidden={!isVisible}
    >
      <div className="mx-auto flex w-full max-w-[600px] items-center gap-2">
        {demandSignalHospitalOriginId ? (
          <DemandSignalButtons
            hospitalOriginId={demandSignalHospitalOriginId}
            variant="bar"
            visible={isVisible}
            className="w-full"
          />
        ) : (
          <>
            <a
              href={`https://map.naver.com/p/search/${mapQuery}`}
              target="_blank"
              rel="noreferrer noopener"
              tabIndex={isVisible ? undefined : -1}
              className="flex flex-1 items-center justify-center rounded-xl border border-gray-200 py-3"
            >
              <Typography as="span" size={15} lineHeight={20} className="font-semibold text-gray-800">
                {t("hero.directions")}
              </Typography>
            </a>
            {tel ? (
              <a
                href={`tel:${tel}`}
                tabIndex={isVisible ? undefined : -1}
                className="flex flex-1 items-center justify-center rounded-xl bg-primary-600 py-3"
              >
                <Typography as="span" size={15} lineHeight={20} className="font-semibold text-white">
                  {t("hero.call")}
                </Typography>
              </a>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

export default HospitalMobileActionBar
